import { waitForPromise } from '@ember/test-waiters';
import { numberToUserDisplay } from '@ebiz/pretty-num';
import * as AditoolsSpice from '@ebiz/aditools-spice/schematics';
import ModelService from '../services/model';
import type IntlService from 'ember-intl/services/intl';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';

type SpicePartsDownloadType = {
  partName: string;
  type: string;
};

const ascAnalysisList: Record<string, string> = {
  AC: 'ACAnalysis.asc',
  NOISE: 'NoiseAnalysis.asc',
  TRAN: 'TransientAnalysis.asc',
};

const INFINITY = 1e66;

const mergePartsArray = function (
  partArray1: Array<SpicePartsDownloadType>,
  partArray2: Array<SpicePartsDownloadType>,
  mergeKey1: string,
  mergeKey2: string,
) {
  // Build list of Parts that contain Netlist, LTspice, or both
  let listOfParts: Array<SpicePartsDownloadType> = [];
  listOfParts = listOfParts.concat(partArray1);
  for (let i = 0; i < partArray2.length; i++) {
    const item = partArray2[i];
    let bFound = false;
    for (let j = 0; j < listOfParts.length; j++) {
      if (item?.partName === listOfParts[j]?.partName) {
        bFound = true;
        const part = listOfParts[j];
        if (part && part.type && part.type.indexOf(mergeKey1) >= 0) {
          part.type = mergeKey1 + '/' + mergeKey2;
          listOfParts[j] = part;
        }
        break;
      }
    }
    if (!bFound && item) {
      listOfParts.push(item);
    }
  }
  return listOfParts;
};

export function getSchematicName(page: string, model: ModelService): string {
  let schematicName = 'Photo';
  let schematicBias = model.state.Bias === '+' ? 'Pos' : 'Neg';
  let schematicStage = model.state.StageCount === 1 || page === 'photodiode' ? '1Stage' : '2Stage';
  let schematicVoltageFollower = '';
  if (
    page !== 'photodiode' &&
    model.state.Bias === '-' &&
    model.schematicModel.stage2 &&
    model.schematicModel.stage2.rf === 0 &&
    model.schematicModel.stage2.rg === INFINITY
  ) {
    schematicVoltageFollower = 'VoltageFollower';
  }
  schematicName = schematicName + schematicBias + schematicStage + schematicVoltageFollower;
  if (page === 'nextsteps') {
    schematicName = schematicName + '_NextSteps';
  }
  return schematicName;
}

export function getAnalysisFiles(pathURL: string, name: string): Record<string, Promise<string>> {
  let promiseArray: Record<string, Promise<string>> = {};
  for (let asc in ascAnalysisList) {
    let url = `${pathURL}/${name}_${ascAnalysisList[asc]}`;
    promiseArray[asc] = getAnalysisFile(url);
  }
  return promiseArray;
}

export function getAnalysisFile(url: string): Promise<string> {
  return waitForPromise(
    fetch(url).then((response) => {
      if (response.status !== 200) {
        throw `HTTP Request Failed reading ${url}: ${response.status} ${response.statusText}`;
      }
      return response.text();
    }),
  );
}

export async function getSpiceSchematicFiles(
  pathURL: string,
  name: string,
  opamps: string[],
  downloadType: string,
  modelService: ModelService,
  intl: IntlService,
  netlistParts: Array<SpicePartsDownloadType>,
): Promise<Array<DownloadFile>> {
  let downloadFiles: Array<DownloadFile> = [];
  let downloadFile: DownloadFile;
  const spicePartsInfo: Record<string, AditoolsSpice.SpicePartInfo> = AditoolsSpiceParts;
  const readmeFile = generateReadMeFile(opamps, netlistParts, spicePartsInfo, downloadType, intl);
  if (Object.entries(readmeFile).length > 0) {
    downloadFiles.push(readmeFile);
    return downloadFiles;
  }
  const schematicValues = getSchematicValues(modelService);
  const responseLimits = getResponseLimits(modelService);
  const analysisFiles = getAnalysisFiles(pathURL, name);
  for (const asc in ascAnalysisList) {
    let schematic = new AditoolsSpice.SpiceSchematic(spicePartsInfo, (await analysisFiles[asc]) as string, 3);
    for (const [index, opamp] of opamps.entries()) {
      const rails = { '+Rail': 'Vp', '-Rail': 'Vn' };
      schematic.updateSymbol(`U${index + 1}`, opamp, true, rails);
    }
    for (const item in schematicValues) {
      const itemValue = schematicValues[item] as number | string;
      schematic.updateSymbolAttribute(
        item,
        'Value',
        typeof itemValue === 'number' ? numberToUserDisplay(itemValue, 'Spice', 3, { showZeroes: false }) : itemValue,
      );
    }
    if (asc == 'AC') {
      const parameters: AditoolsSpice.SweepParameters = {
        sweepType: 'decade',
        points: 100,
        startFrequency: responseLimits['minimumFrequency'],
        stopFrequency: responseLimits['maximumFrequency'],
      };
      schematic.updateACAnalysis(parameters);
    }
    if (asc == 'NOISE') {
      const parameters: AditoolsSpice.SweepParameters = {
        sweepType: 'decade',
        points: 100,
        startFrequency: responseLimits['minimumFrequency'],
        stopFrequency: responseLimits['maximumFrequency'],
      };
      schematic.updateNoiseAnalysis(parameters);
    }
    if (asc == 'TRAN') {
      const parameters: AditoolsSpice.TransientParameters = {
        step: 0.000000001,
        stopTime: (responseLimits['maximumTimeAxis'] as number) / 2,
      };
      schematic.updateTransientAnalysis(parameters);

      const pulseParameters: AditoolsSpice.PulseSourceParameters = {
        lowValue: 0,
        highValue: modelService.state.Ip,
        delayTime: 0.000000001,
        riseTime: 0.000000001,
        fallTime: 0.000000001,
        pulseWidth: 1,
        period: 2,
      };
      schematic.updatePulseCurrentSource('Ip', pulseParameters);
    }
    downloadFile = {
      name: ascAnalysisList[asc],
      value: schematic.schematic,
      location: downloadType == 'all' ? 'SPICE Files\\LTSpice' : 'LTSpice',
      type: 'LTSPICE',
    };
    downloadFiles.push(downloadFile);
  }
  return downloadFiles;
}

function getSchematicValues(modelService: ModelService): Record<string, string | number> {
  let schematicValues: Record<string, string | number> = {};
  let model = modelService;
  schematicValues['Cd'] = model.state.Cd;
  schematicValues['Rsh'] = model.state.Rsh;
  schematicValues['V3'] =
    (model.state.Bias == '-' ? '-' : '') + numberToUserDisplay(model.state.Vr, 'Spice', 5, { showZeroes: false });
  schematicValues['V2'] = numberToUserDisplay(-model.powerSupplyRange.max, 'Spice', 5, { showZeroes: false });
  schematicValues['V1'] = numberToUserDisplay(model.powerSupplyRange.max, 'Spice', 5, { showZeroes: false });
  if (model.schematicModel.stage1) {
    schematicValues['Cf'] = model.schematicModel.stage1.cf;
    schematicValues['Rf'] = model.schematicModel.stage1.rf;
  }
  if (model.schematicModel.stage2) {
    schematicValues['Cfilter'] = model.schematicModel.stage2.cfilter;
    if (model.state.Bias == '+') {
      schematicValues['Rf2'] = model.schematicModel.stage2.rf;
      schematicValues['Rg2'] = model.schematicModel.stage2.rg;
    }
    if (model.state.Bias == '-') {
      if (model.schematicModel.stage2.rf === 0 && model.schematicModel.stage2.rg === INFINITY) {
        // This is a voltage follower circuit, which has no rf or rg on the second stage.
        schematicValues['Rfilter'] = model.schematicModel.stage2.rfilter;
      } else {
        schematicValues['Rfilter'] = model.schematicModel.stage2.rfilter;
        schematicValues['Rf2'] = model.schematicModel.stage2.rf;
        schematicValues['Rg2'] = model.schematicModel.stage2.rg;
      }
    }
  }
  return schematicValues;
}

function getResponseLimits(modelService: ModelService): Record<string, number> {
  return {
    minimumFrequency: modelService.frequencyResponseVA.graph.limits.xmin,
    maximumFrequency: modelService.frequencyResponseVA.graph.limits.xmax,
    minimumTimeAxis: modelService.pulseResponse.graph.limits.xmin,
    maximumTimeAxis: modelService.pulseResponse.graph.limits.xmax,
  };
}

function generateReadMeFile(
  opamps: string[],
  netlistParts: Array<SpicePartsDownloadType>,
  spicePartsInfo: Record<string, AditoolsSpice.SpicePartInfo>,
  downloadType: string,
  intl: IntlService,
): DownloadFile {
  let result: DownloadFile = {};
  let fileName = '';
  let content = '';
  const schematicParts: Array<SpicePartsDownloadType> = [];
  let _opamps = [...new Set(opamps)];
  let hasLTspice = true;

  for (const _opamp of _opamps) {
    if (typeof spicePartsInfo[_opamp] !== 'undefined') {
      schematicParts.push({ partName: _opamp, type: 'LTspice' }); // This part has a spice resource
    } else {
      hasLTspice = false;
    }
  }
  const listOfParts = mergePartsArray(netlistParts, schematicParts, 'Netlist', 'LTspice');
  const opAmpNoSpice = _opamps.filter((opAmpName) => {
    const searchpart = listOfParts.find(function (item) {
      return item.partName === opAmpName;
    });
    return typeof searchpart === 'undefined' || searchpart === null;
  });
  const noSpiceFiles = opAmpNoSpice.length > 0;
  const opAmpsNoNetlist = listOfParts.find(function (item) {
    return !item.type.includes('Netlist');
  });
  const hasNetList = opAmpsNoNetlist === undefined;

  if (noSpiceFiles) {
    fileName = intl.t('download.spice-files.message_part_no_spice_info_filename');
    content = intl.t('download.spice-files.message_part_no_spice_info');
    // Add list of Non-Supported Parts
    for (let k = 0; k < opAmpNoSpice.length; k++) {
      content += '\r\n' + opAmpNoSpice[k];
    }
    // Add Message File
    result = {
      name: fileName,
      value: content,
      location: '',
      type: 'LTSPICE',
    };
  } else {
    if (!hasLTspice) {
      fileName = hasNetList
        ? intl.t('download.spice-files.message_no_schematic_filename')
        : intl.t('download.spice-files.message_no_schematic_no_netlist_filename');
      content = hasNetList
        ? intl.t('download.spice-files.message_no_schematic')
        : intl.t('download.spice-files.message_no_schematic_no_netlist');
      // Add list of Supported Parts
      for (let k = 0; k < listOfParts.length; k++) {
        content += '\r\n' + listOfParts[k]?.partName + ': ' + listOfParts[k]?.type;
      }
      // Add Message File
      result = {
        name: fileName,
        value: content,
        location: downloadType === 'all' ? 'SPICE Files' : '',
        type: 'LTSPICE',
      };
    }
  }
  return result;
}
